import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { CORE_WINDOW_CONFIG, CoreWindowConfig } from './config';
import { WindowService } from './window.service';

export interface ShellViewport {
  scrollY: number;
  viewportHeight: number;
  iframeOffsetTop: number;
  iframeHeight: number;
}

@Injectable({ providedIn: 'root' })
export class IframeMessageBus {
  /**
   * Emits whenever the shell broadcasts its viewport state (scroll position,
   * viewport height, and the iframe's offset within the shell document).
   * Used by Angular components to correctly position overlays/modals that
   * depend on document-level dimensions rather than the iframe viewport.
   */
  readonly viewport$: Observable<ShellViewport> = new Observable<ShellViewport>(
    (subscriber) => {
      const handler = (event: MessageEvent) => {
        if (event.data?.id === 'shell.viewport') {
          subscriber.next(event.data as ShellViewport);
        }
      };
      this._window.addEventListener('message', handler);
      return () => this._window.removeEventListener('message', handler);
    },
  ).pipe(shareReplay(1));

  constructor(
    @Inject(CORE_WINDOW_CONFIG) private _config: CoreWindowConfig,
    private _window: WindowService,
  ) {}

  sendToParent(eventName: string, payload: object = {}) {
    if (this._config.isEmbedded) {
      this._window.parent.postMessage({
        id: ['iframe', eventName].join('.'),
        ...payload,
      });
    }
  }

  setParentTitle(title: string) {
    this.sendToParent('setTitle', { title });
  }

  triggerParentNavigation(route: string) {
    this.sendToParent('navigate', { route });
  }

  show() {
    this.sendToParent('show');
  }

  requestToken() {
    this.sendToParent('requestToken');
  }

  resize(height: number) {
    this.sendToParent('resize', { height });
  }

  onModalOpen(top: number) {
    this.sendToParent('onModalOpen', { top });
  }

  onModalClose() {
    this.sendToParent('onModalClose');
  }

  /**
   * Scrolls the shell document to an element identified by a CSS selector.
   * Used when the target element lives outside the iframe (e.g. the shell footer).
   * No-op when not running in the embedded iframe context.
   */
  scrollToShellElement(selector: string) {
    this.sendToParent('scrollToElement', { selector });
  }

  /**
   * Requests a one-time shell.viewport snapshot from the shell.
   * The response arrives via the viewport$ observable.
   */
  requestViewport() {
    this.sendToParent('getViewport');
  }

  triggerCustomEvent(eventName: string, param?: unknown) {
    this.sendToParent(
      eventName,
      ...(param !== undefined ? [{ ...param }] : []),
    );
  }
}
